import { api } from '@/lib/api'
import type { RequestOptions } from '@/lib/api'
import type { DataEnvelope, Entitlements } from '@/lib/types'
import type {
  AdminAuditEntry,
  AdminList,
  AdminOverview,
  AdminUser,
  AdminWorkspace,
  AdminWorkspaceDetail,
  GrantInput,
} from './types'

/**
 * The platform admin console's client.
 *
 * Two things are worth knowing about the shape of this module.
 *
 * First, a grant is NOT issued through `/api/v2/admin/*`. It goes to
 * `POST /api/billing/orgs/{slug}/grants`, which already existed, already writes
 * `entitlement_grants`, and is already read by the entitlement resolver — so a
 * comp takes effect everywhere the moment it returns. Adding a second grant
 * endpoint would have meant two code paths that could disagree about what a
 * paid plan is. Revoking is a console route because there was no revoke before.
 *
 * Second, every route here answers 404 — not 403 — to a caller without
 * `users.is_admin`. So an ApiError with `isNotFound` from this module means
 * "you are not a platform administrator" just as often as it means "no such
 * workspace", and the screens treat it as the former.
 */

const adminPath = '/api/v2/admin'

function seg(value: string): string {
  return encodeURIComponent(value)
}

export const adminApi = {
  overview: (options?: RequestOptions) =>
    api.get<DataEnvelope<AdminOverview>>(`${adminPath}/overview`, options).then((r) => r.data),

  workspaces: (query: { q?: string; page?: number; per_page?: number }, options?: RequestOptions) =>
    api.get<AdminList<AdminWorkspace>>(`${adminPath}/workspaces`, { ...options, query }),

  workspace: (slug: string, options?: RequestOptions) =>
    api
      .get<DataEnvelope<AdminWorkspaceDetail>>(`${adminPath}/workspaces/${seg(slug)}`, options)
      .then((r) => r.data),

  users: (query: { q?: string; page?: number; per_page?: number }, options?: RequestOptions) =>
    api.get<AdminList<AdminUser>>(`${adminPath}/users`, { ...options, query }),

  audit: (query: { page?: number; per_page?: number } = {}, options?: RequestOptions) =>
    api.get<AdminList<AdminAuditEntry>>(`${adminPath}/audit`, { ...options, query }),

  /**
   * Promote or demote a platform administrator.
   *
   * The server refuses self-demotion and the demotion of the last remaining
   * administrator, both with a 422 whose message is written for a person — so
   * the UI shows it verbatim rather than pre-empting either rule.
   */
  // Setting somebody's password is account takeover, performed deliberately.
  // The server requires a reason, applies the same strength rules as
  // registration, revokes every session of that user, and refuses when the
  // target is the caller.
  setPassword: (userId: number, input: { password: string; reason: string }) =>
    api
      .post<DataEnvelope<{ password_set: boolean; email: string; sessions_revoked: boolean }>>(
        `${adminPath}/users/${userId}/password`,
        input,
      )
      .then((r) => r.data),

  setAdmin: (userId: number, input: { is_admin: boolean; reason: string }) =>
    api
      .post<DataEnvelope<AdminUser>>(`${adminPath}/users/${userId}/admin`, input)
      .then((r) => r.data),

  /**
   * Comp a workspace onto a paid plan.
   *
   * This is the pre-existing billing route, not a console one. `reason` is
   * mandatory server-side; a request without it comes back 422 with the error
   * on the `reason` field.
   */
  grant: (slug: string, input: GrantInput) =>
    api.post<{ entitlements: Entitlements }>(`/api/billing/orgs/${seg(slug)}/grants`, input),

  /**
   * Withdraw a manual grant.
   *
   * The row is marked, never deleted, so the history of who comped a workspace
   * survives. The workspace then falls back to whatever Stripe or Apple say,
   * which may be Free.
   *
   * The reason rides in the query string as well as the body: a DELETE body is
   * legal but not universally forwarded, and this deployment sits behind an
   * Apache proxy.
   */
  revokeGrant: (grantId: number, reason: string) =>
    api.delete<DataEnvelope<{ revoked: boolean; grant_id: number; entitlements: Entitlements | null }>>(
      `${adminPath}/grants/${grantId}`,
      { query: { reason } },
    ),
}
